import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { GraduationCap, X, Check, ChevronRight, RotateCcw } from "lucide-react";

const STORAGE_KEY = "misco_training_progress";

const TASKS = [
  { id: "dashboard", title: "Explore the Dashboard", hint: "See live counts, top issues and recent activity.", to: "/" },
  { id: "new", title: "Log a new rework request", hint: "Enter a job & part number, pick a reason, and submit.", to: "/new" },
  { id: "register", title: "Find it in the Register", hint: "Search by job or part number and open the record.", to: "/register" },
  { id: "advance", title: "Advance a part on the Board", hint: "Move a card Pending → Optimising → At Machine.", to: "/board" },
  { id: "hold", title: "Put a part On Hold", hint: "Use the Hold action and enter a reason.", to: "/board" },
  { id: "rack", title: "Clear a finished part", hint: "In Rack Clearing, send a completed part to Maintenance.", to: "/rack" },
  { id: "maintenance", title: "Mark it Left Factory", hint: "In Maintenance Clearing, record who collected it.", to: "/maintenance" },
  { id: "scan", title: "Scan a QR label", hint: "Use the Scan station to jump straight to a part.", to: "/scan" },
];

export const TrainingScenarios = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); } catch { return new Set(); }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...done]));
  }, [done]);

  if (process.env.REACT_APP_TRAINING_MODE !== "true") return null;
  if (location.pathname === "/login") return null;

  const toggle = (id) => setDone((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const reset = () => setDone(new Set());
  const completed = TASKS.filter((t) => done.has(t.id)).length;
  const pct = Math.round((completed / TASKS.length) * 100);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="training-scenarios-toggle"
        className="fixed bottom-24 right-4 z-[60] flex items-center gap-2 rounded-full bg-[#424e43] px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#f7f5f1] shadow-lg transition-transform duration-150 hover:bg-[#232826] active:scale-95"
      >
        <GraduationCap size={16} />
        Training {completed}/{TASKS.length}
      </button>
    );
  }

  return (
    <div
      data-testid="training-scenarios-panel"
      className="fixed bottom-24 right-4 z-[60] w-[min(92vw,360px)] overflow-hidden rounded-sm border border-[#cfcfc9] bg-white shadow-2xl"
    >
      <div className="flex items-center gap-2 bg-[#232826] px-4 py-3 text-[#f7f5f1]">
        <GraduationCap size={16} />
        <span className="font-display text-sm font-bold">Guided training</span>
        <span className="ml-auto font-mono text-xs text-[#cfcfc9]" data-testid="training-progress">{completed}/{TASKS.length}</span>
        <button type="button" onClick={() => setOpen(false)} data-testid="training-scenarios-close" className="ml-1 text-[#cfcfc9] hover:text-white">
          <X size={16} />
        </button>
      </div>

      <div className="h-1 w-full bg-[#e6e5df]">
        <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      <ol className="max-h-[52vh] divide-y divide-[#e6e5df] overflow-y-auto">
        {TASKS.map((t, i) => {
          const isDone = done.has(t.id);
          return (
            <li key={t.id} className="flex items-start gap-3 px-3 py-3" data-testid={`training-task-${t.id}`}>
              <button
                type="button"
                onClick={() => toggle(t.id)}
                data-testid={`training-check-${t.id}`}
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-colors ${
                  isDone ? "border-emerald-500 bg-emerald-500 text-white" : "border-[#cfcfc9] bg-white text-transparent hover:border-[#647265]"
                }`}
              >
                <Check size={13} strokeWidth={3} />
              </button>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium ${isDone ? "text-[#a39f98] line-through" : "text-[#232826]"}`}>
                  {i + 1}. {t.title}
                </div>
                <div className="text-xs text-[#6b6a68]">{t.hint}</div>
              </div>
              <Link
                to={t.to}
                onClick={() => setOpen(false)}
                data-testid={`training-go-${t.id}`}
                className="mt-0.5 flex items-center gap-0.5 whitespace-nowrap text-[11px] font-bold uppercase tracking-widest text-[#424e43] hover:text-[#232826]"
              >
                Open <ChevronRight size={13} />
              </Link>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-between border-t border-[#e6e5df] bg-[#f7f5f1] px-3 py-2">
        <span className="text-[11px] text-[#6b6a68]">
          {completed === TASKS.length ? "All done — nice work! 🎉" : "Tick tasks as you complete them."}
        </span>
        <button type="button" onClick={reset} data-testid="training-reset" className="flex items-center gap-1 text-[11px] font-medium text-[#8a3a2a] hover:text-[#6a2a1e]">
          <RotateCcw size={12} /> Reset
        </button>
      </div>
    </div>
  );
};
